import VisuallyHiddenText from '@UI/Pages/Home/Shared/VisuallyHiddenText';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type IndicatorComparisonView from '@UI/Store/Indicators/Views/IndicatorComparisonView';
import type * as React from 'react';

const lineStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.84375rem;
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.5;
	color: ${theme.colors.inkSecondary};
`;

const MINUS = '−';

const formatSigned = (value: number): string => (value < 0 ? `${MINUS}${Math.abs(value)}` : `+${value}`);

type Props = {
	readonly comparison: IndicatorComparisonView;
	readonly days: number;
	/** What is counted, singular, for the "new" case: "document", "bénéficiaire créé"… */
	readonly subject: string;
	/** Formats the absolute difference; defaults to the raw number. */
	readonly formatValue?: (value: number) => string;
};

/** Neutral ink on purpose: a drop is not an alarm and a rise is not a success. */
const ComparisonLine: React.FC<Props> = ({ comparison, days, subject, formatValue = String }) => {
	const { direction, difference, percent } = comparison;
	const reference = `par rapport aux ${days} jours précédents`;

	if (direction === 'none') {
		return null;
	}

	if (direction === 'new') {
		const text = `Nouveau : aucun ${subject} sur les ${days} jours précédents`;

		return (
			<p css={lineStyle}>
				<span aria-hidden="true">{text}</span>
				<VisuallyHiddenText>{text}</VisuallyHiddenText>
			</p>
		);
	}

	if (direction === 'stable') {
		return (
			<p css={lineStyle}>
				<span aria-hidden="true">{`= stable ${reference}`}</span>
				<VisuallyHiddenText>{`Stable ${reference}`}</VisuallyHiddenText>
			</p>
		);
	}

	const percentText = percent === null ? '' : ` (${formatSigned(percent)} %)`;
	const visible = `${direction === 'up' ? '▲' : '▼'} ${difference < 0 ? MINUS : '+'}${formatValue(Math.abs(difference))} ${reference}${percentText}`;
	const spoken = `${direction === 'up' ? 'En hausse' : 'En baisse'} de ${formatValue(Math.abs(difference))} ${reference}${
		percent === null ? '' : `, soit ${formatSigned(percent)} pour cent`
	}`;

	return (
		<p css={lineStyle}>
			<span aria-hidden="true">{visible}</span>
			<VisuallyHiddenText>{spoken}</VisuallyHiddenText>
		</p>
	);
};

export default ComparisonLine;
